import React, {useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex} from '@salesforce/retail-react-app/app/components/shared/ui'
import Swatch from '../../swatch-group/swatch'
import {filterImageGroups, getDecoratedVariationAttributes} from '../../../utils/product-utils'
import {useProductAvailability} from '../../../hooks/use-product-availability'

const ProductTileDetailsColorSwatches = ({
    product,
    styles,
    colorValue,
    setColorValue,
    setProductId,
    productId
}) => {
    const sourceProduct = useMemo(() => {
        if (product?.variationAttributes?.length && product?.imageGroups?.length) {
            return product
        }
        if (product?.representedProduct?.variationAttributes?.length) {
            return product.representedProduct
        }
        return product
    }, [product])

    const decoratedVariationAttributes = useMemo(
        () => getDecoratedVariationAttributes(sourceProduct) || [],
        [sourceProduct]
    )

    const colorAttribute = useMemo(() => {
        const decoratedColor = decoratedVariationAttributes.find(({id}) => id === 'color')
        if (decoratedColor) return decoratedColor
        return sourceProduct?.variationAttributes?.find(({id}) => id === 'color')
    }, [decoratedVariationAttributes, sourceProduct?.variationAttributes])

    const values = colorAttribute?.values || []

    const derivedValues = useMemo(() => {
        if (values.length) return values

        const variationGroups =
            product?.variationGroups ||
            product?.variants ||
            sourceProduct?.variationGroups ||
            sourceProduct?.variants ||
            []

        const colorValues = variationGroups
            .map((group) => group?.variationValues?.color)
            .filter(Boolean)

        const uniqueColorValues = Array.from(new Set(colorValues))

        if (!uniqueColorValues.length) return []

        return uniqueColorValues.map((value) => {
            const swatchImage = filterImageGroups(sourceProduct?.imageGroups, {
                viewType: 'swatch',
                variationValues: {color: value}
            })?.[0]?.images?.[0]

            return {
                value,
                name: value,
                swatch: swatchImage
            }
        })
    }, [values, product?.variationGroups, product?.variants, sourceProduct])

    const selectedColorFromProductId = useMemo(() => {
        if (!productId) return null
        const variationGroups =
            product?.variationGroups ||
            product?.variants ||
            sourceProduct?.variationGroups ||
            sourceProduct?.variants ||
            []
        const matchedGroup = variationGroups.find((group) => group?.productId === productId)
        return matchedGroup?.variationValues?.color || null
    }, [productId, product, sourceProduct])

    const selectedColor =
        colorValue ||
        selectedColorFromProductId ||
        sourceProduct?.variationValues?.color ||
        product?.variationValues?.color ||
        sourceProduct?.c_color ||
        product?.c_color ||
        null

    const handleColorChange = useCallback(
        (value) => {
            if (!value) return

            const variationGroups =
                product?.variationGroups ||
                product?.variants ||
                sourceProduct?.variationGroups ||
                sourceProduct?.variants ||
                []
            const matchedGroup = variationGroups.find(
                (group) => group?.variationValues?.color === value
            )

            if (matchedGroup?.productId) {
                setProductId?.(matchedGroup.productId)
            }

            if (setColorValue) {
                setColorValue(value)
            }
        },
        [product?.variationGroups, product?.variants, setProductId, setColorValue]
    )

    // Order by the represented (default) color — stable across selection and remounts, so the
    // row doesn't reshuffle on interaction.
    const orderingColor =
        product?.representedProduct?.c_color ||
        sourceProduct?.representedProduct?.c_color ||
        sourceProduct?.variationValues?.color ||
        product?.variationValues?.color ||
        sourceProduct?.c_color ||
        product?.c_color ||
        null

    // Map each color -> its variation group product id so we can look up per-color availability
    // and hide colors that are entirely out of stock (same idea as the PDP color swatches).
    const colorToProductId = useMemo(() => {
        const variationGroups =
            product?.variationGroups ||
            product?.variants ||
            sourceProduct?.variationGroups ||
            sourceProduct?.variants ||
            []
        const map = {}
        variationGroups.forEach((group) => {
            const color = group?.variationValues?.color
            if (color && group?.productId && !(color in map)) {
                map[color] = group.productId
            }
        })
        return map
    }, [product?.variationGroups, product?.variants, sourceProduct])

    const colorGroupIds = useMemo(() => Object.values(colorToProductId), [colorToProductId])

    // Real-time availability per color (variation group). Only fetch when the tile shows more than
    // one color (single-color tiles render no swatch row). A longer staleTime avoids a burst of
    // requests when many tiles mount together on a PLP.
    const {data: colorAvailabilityMap, isFetched: isColorAvailabilityFetched} =
        useProductAvailability(colorGroupIds, {
            enabled: colorGroupIds.length > 1,
            staleTime: 5 * 60 * 1000
        })

    // Swatches mount on hover, so the availability request starts then. Until it has resolved at
    // least once, treat the color set as pending and hold the row back — otherwise every color
    // paints on hover and the out-of-stock ones visibly disappear a moment later. Once fetched
    // (even from the 5 min cache on a later hover) it renders once, already filtered.
    const isColorAvailabilityPending = colorGroupIds.length > 1 && !isColorAvailabilityFetched

    const isColorOrderable = useCallback(
        (colorValue) => {
            const groupProductId = colorToProductId[colorValue]
            const orderable = groupProductId
                ? colorAvailabilityMap?.[groupProductId]?.inventory?.orderable
                : undefined
            // Fail open: keep the color unless availability explicitly reports it not orderable.
            return orderable !== false
        },
        [colorToProductId, colorAvailabilityMap]
    )

    // Colors that have at least one variant configured for notify-me (restock). Such a color stays
    // visible even when it is entirely out of stock, so shoppers can still request a restock alert.
    // Computed on the backend (search hit `c_notifyMeColors`) since the tile only has swatch-level data.
    const notifyMeColors = product?.c_notifyMeColors || sourceProduct?.c_notifyMeColors

    // Up to 5 swatches; filter offline before slicing, then pin orderingColor first.
    const swatchValues = useMemo(() => {
        const list = (values.length ? values : derivedValues).filter(
            (value) =>
                value?.online !== false &&
                (isColorOrderable(value?.value) || notifyMeColors?.includes(value?.value))
        )
        const selectedIndex = list.findIndex((value) => value?.value === orderingColor)
        const ordered =
            selectedIndex > 0
                ? [
                      list[selectedIndex],
                      ...list.slice(0, selectedIndex),
                      ...list.slice(selectedIndex + 1)
                  ]
                : list
        return ordered.slice(0, 5)
    }, [values, derivedValues, orderingColor, isColorOrderable, notifyMeColors])

    if (isColorAvailabilityPending) {
        return null
    }

    if (!swatchValues.length) {
        return null
    }

    return (
        <Box
            {...styles.colorSwatchCarouselContainer}
            onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
            }}
            onMouseDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
            }}
        >
            <Flex {...styles.colorSwatchRow} role="radiogroup" aria-label="Colors">
                {swatchValues.map((value) => {
                    if (value?.online === false) {
                        return null
                    }

                    const swatchImage = value?.image || value?.swatch
                    const swatchImageUrl = swatchImage?.disBaseLink || swatchImage?.link
                    const isSelected = selectedColor === value?.value

                    const content = swatchImageUrl ? (
                        <Box
                            {...styles.colorSwatchImage}
                            backgroundColor={
                                typeof value?.name === 'string'
                                    ? value.name.toLowerCase()
                                    : undefined
                            }
                            backgroundImage={`url(${swatchImageUrl})`}
                        />
                    ) : (
                        <Box {...styles.colorSwatchText}>{value?.name}</Box>
                    )

                    return (
                        <Swatch
                            key={value?.value}
                            value={value?.value}
                            name={value?.name}
                            isProductTile
                            variant="circle"
                            selected={isSelected}
                            disabled={value?.orderable === false}
                            isFocusable={true}
                            handleSelect={handleColorChange}
                            {...styles.colorSwatchItem}
                        >
                            {content}
                        </Swatch>
                    )
                })}
            </Flex>
        </Box>
    )
}

ProductTileDetailsColorSwatches.propTypes = {
    product: PropTypes.object,
    styles: PropTypes.object,
    colorValue: PropTypes.string,
    setColorValue: PropTypes.func,
    setProductId: PropTypes.func,
    productId: PropTypes.string
}

export default ProductTileDetailsColorSwatches
